import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Server, Building2, Cloud, Laptop, CheckCircle2, Activity } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/site/primitives";
import { useI18n } from "@/i18n";

const NODES = [
  { id: "hq", label: "HQ Bucureşti", type: "Nod central", type_en: "Central node", icon: Server, x: 50, y: 52,
    subnet: "10.10.0.0/24", latency: "—", services: ["pfSense", "Proxmox", "PBS", "Multi-WAN"],
    note: "Gateway central cu firewall, virtualizare și backup. Failover automat pe 2 linii de internet.",
    note_en: "Central gateway with firewall, virtualization and backup. Automatic failover across 2 internet lines." },
  { id: "cluj", label: "Sediu Cluj", label_en: "Cluj office", type: "Filială", type_en: "Branch", icon: Building2, x: 18, y: 24,
    subnet: "10.20.0.0/24", latency: "8 ms", services: ["Switch L3", "AP WiFi", "VLAN-uri"],
    note: "Interconectat prin tunel VPN site-to-site securizat cu HQ.",
    note_en: "Interconnected with HQ through a secure site-to-site VPN tunnel." },
  { id: "tim", label: "Sediu Timişoara", label_en: "Timișoara office", type: "Filială", type_en: "Branch", icon: Building2, x: 18, y: 82,
    subnet: "10.30.0.0/24", latency: "11 ms", services: ["NAS", "Cameră Dahua", "Control acces"],
    note: "Supraveghere și control acces integrate, management centralizat.",
    note_en: "Integrated surveillance and access control, centrally managed." },
  { id: "cloud", label: "Datacenter / Cloud", type: "Private cloud", type_en: "Private cloud", icon: Cloud, x: 82, y: 24,
    subnet: "10.90.0.0/24", latency: "6 ms", services: ["Docker", "Nextcloud", "HAProxy", "SSL"],
    note: "Aplicații self-hosted publicate securizat prin reverse proxy și Cloudflare.",
    note_en: "Self-hosted apps published securely through reverse proxy and Cloudflare." },
  { id: "remote", label: "Utilizatori remote", label_en: "Remote users", type: "Acces VPN", type_en: "VPN access", icon: Laptop, x: 82, y: 82,
    subnet: "10.99.0.0/24", latency: "variabil", latency_en: "variable", services: ["WireGuard", "MFA", "Zero-Trust"],
    note: "Angajați conectați securizat de oriunde, cu autentificare în doi pași.",
    note_en: "Employees connected securely from anywhere, with two-factor authentication." },
];

const CENTER = NODES[0];

export const NetworkMap = () => {
  const [sel, setSel] = useState("hq");
  const { t, lang } = useI18n();
  const node = NODES.find((n) => n.id === sel);
  const nodeType = lang === "en" ? node.type_en : node.type;
  const nodeLabel = lang === "en" && node.label_en ? node.label_en : node.label;
  const nodeNote = lang === "en" ? node.note_en : node.note;
  const nodeLatency = lang === "en" && node.latency_en ? node.latency_en : node.latency;

  return (
    <section id="retea" data-testid="network-section" className="relative border-t border-white/10 bg-ink-900">
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="max-w-3xl">
          <Reveal><SectionLabel index="[ • ]">{t("net.label")}</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              {t("net.h1")} <span className="text-aurora">{t("net.hkw")}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-xl font-body text-base leading-relaxed text-white/55">
              {t("net.sub")}
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3 lg:items-stretch">
          {/* Map */}
          <Reveal delay={0.12} className="lg:col-span-2">
            <div className="card-edge relative h-[400px] overflow-hidden rounded-2xl sm:h-[460px]">
              <div className="absolute inset-0 grid-lines opacity-40" />
              <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
                {NODES.slice(1).map((n) => (
                  <line key={n.id} x1={CENTER.x} y1={CENTER.y} x2={n.x} y2={n.y}
                    stroke={sel === n.id || sel === "hq" ? "#00E5FF" : "#2b3a4f"}
                    strokeOpacity={sel === n.id || sel === "hq" ? 0.9 : 0.4}
                    strokeWidth="1.5" vectorEffect="non-scaling-stroke"
                    className={sel === n.id || sel === "hq" ? "flow-line" : ""} />
                ))}
              </svg>

              {NODES.map((n) => {
                const Icon = n.icon;
                const active = sel === n.id;
                return (
                  <button
                    key={n.id}
                    data-testid={`node-${n.id}`}
                    onClick={() => setSel(n.id)}
                    style={{ left: `${n.x}%`, top: `${n.y}%` }}
                    className="group absolute -translate-x-1/2 -translate-y-1/2"
                  >
                    <span className={`grid h-14 w-14 place-items-center rounded-full border backdrop-blur-md transition-all duration-300 ${active ? "border-cyan-neon bg-cyan-neon/15 text-cyan-neon shadow-[0_0_24px_rgba(0,229,255,0.5)] scale-110" : "border-white/15 bg-ink-800/80 text-white/60 hover:border-cyan-neon/50 hover:text-cyan-neon"}`}>
                      <Icon size={22} />
                    </span>
                    <span className={`mt-2 block whitespace-nowrap text-center font-mono text-[10px] uppercase tracking-wider transition-colors duration-300 ${active ? "text-cyan-neon" : "text-white/50"}`}>
                      {lang === "en" && n.label_en ? n.label_en : n.label}
                    </span>
                    {n.id === "hq" && (
                      <span className="pointer-events-none absolute left-1/2 top-7 -z-10 h-14 w-14 -translate-x-1/2 -translate-y-1/2 animate-ping rounded-full bg-cyan-neon/20" />
                    )}
                  </button>
                );
              })}
            </div>
          </Reveal>

          {/* Detail panel */}
          <Reveal delay={0.18}>
            <div className="card-edge flex h-full flex-col rounded-2xl p-7">
              <AnimatePresence mode="wait">
                <motion.div key={node.id}
                  initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.35 }}>
                  <span className="inline-flex items-center gap-2 rounded-full border border-cyan-neon/30 bg-cyan-neon/10 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-cyan-neon">
                    <node.icon size={12} /> {nodeType}
                  </span>
                  <h3 className="mt-4 font-display text-2xl font-extrabold text-white">{nodeLabel}</h3>

                  <div className="mt-5 flex flex-col gap-3 border-y border-white/10 py-5 font-mono text-sm">
                    <div className="flex justify-between"><span className="text-white/40">{t("net.subnet")}</span><span className="text-cyan-neon">{node.subnet}</span></div>
                    <div className="flex justify-between"><span className="text-white/40">{t("net.latency")}</span><span className="text-white/80">{nodeLatency}</span></div>
                    <div className="flex items-center justify-between"><span className="text-white/40">{t("net.state")}</span><span className="flex items-center gap-1.5 text-emerald-400"><CheckCircle2 size={14} /> {t("net.online")}</span></div>
                  </div>

                  <div className="mt-5">
                    <div className="mb-2 font-mono text-[10px] uppercase tracking-widest text-white/40">{t("net.services")}</div>
                    <div className="flex flex-wrap gap-2">
                      {node.services.map((sv) => (
                        <span key={sv} className="rounded border border-white/10 bg-ink-900/60 px-2.5 py-1 font-mono text-[11px] text-white/70">{sv}</span>
                      ))}
                    </div>
                  </div>

                  <p className="mt-5 flex gap-2 font-body text-sm leading-relaxed text-white/55">
                    <Activity size={16} className="mt-0.5 shrink-0 text-cyan-neon" /> {nodeNote}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
